import { useState, useEffect, useContext, useCallback } from 'react';
import { Radio, Terminal, RefreshCw, AlertCircle, Cpu } from 'lucide-react';
import { WidgetContext } from '../components/WidgetWrapper';
import Modal from '../../../components/ui/Modal';

interface ProfileInfo {
    name: string;
    type: string;
    data: string;
    state: string;
}

interface SofiaData {
    online: boolean;
    profilesCount: number;
    profiles: ProfileInfo[];
    error?: string;
}

interface SofiaStatusWidgetProps {
    config?: Record<string, any>;
    isEditMode?: boolean;
    onConfigChange?: (config: Record<string, any>) => void;
    registerConfigHandler?: (handler: () => void) => void;
}

// Default widget preferences for card styling
const DEFAULT_CARD_COLOR = '#1e293b';
const DEFAULT_BORDER_COLOR = '#334155';

export function SofiaStatusWidget({ config, isEditMode, onConfigChange, registerConfigHandler }: SofiaStatusWidgetProps) {
    const [data, setData] = useState<SofiaData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
    const [profileConfig, setProfileConfig] = useState<string | null>(null);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [configError, setConfigError] = useState<string | null>(null);

    const widgetPrefs = useContext(WidgetContext);
    const cardColor = widgetPrefs?.cardColor || DEFAULT_CARD_COLOR;
    const borderColor = widgetPrefs?.borderColor || DEFAULT_BORDER_COLOR;

    const fetchSofiaStatus = useCallback(async () => {
        try {
            const protocol = window.location.protocol;
            const host = window.location.host;
            const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/sofia/status`);

            if (response.ok) {
                const resData = await response.json();
                if (resData.success && resData.data) {
                    setData(resData.data);
                    setError(null);
                } else {
                    setError(resData.error || 'Failed to fetch Sofia status');
                }
            } else {
                setError(`HTTP ${response.status}: Failed to load status`);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to connect to monitoring service');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchProfileConfig = useCallback(async (profileName: string) => {
        setLoadingConfig(true);
        setConfigError(null);
        setProfileConfig(null);
        try {
            const protocol = window.location.protocol;
            const host = window.location.host;
            const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/sofia/profile/${profileName}`);

            if (response.ok) {
                const resData = await response.json();
                if (resData.success && resData.data) {
                    setProfileConfig(resData.data.raw);
                } else {
                    setConfigError(resData.error || 'Failed to fetch profile configuration');
                }
            } else {
                setConfigError(`HTTP ${response.status}: Failed to load configuration`);
            }
        } catch (err: any) {
            setConfigError(err.message || 'Failed to connect to server');
        } finally {
            setLoadingConfig(false);
        }
    }, []);

    useEffect(() => {
        fetchSofiaStatus();
        const interval = setInterval(fetchSofiaStatus, 10000);
        return () => clearInterval(interval);
    }, [fetchSofiaStatus]);

    const handleProfileClick = (profileName: string) => {
        setSelectedProfile(profileName);
        fetchProfileConfig(profileName);
    };

    const handleCloseModal = () => {
        setSelectedProfile(null);
        setProfileConfig(null);
        setConfigError(null);
    };

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center p-4">
                <div className="flex flex-col items-center gap-3">
                    <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                    <span className="text-sm text-muted-foreground font-medium">Checking FreeSWITCH Sofia status...</span>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="h-full flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
                <AlertCircle className="w-10 h-10 text-destructive mb-3" />
                <p className="text-sm font-medium mb-1">Could not fetch Sofia status</p>
                <p className="text-xs opacity-75 max-w-[250px] mb-4">{error || 'No data returned'}</p>
                <button
                    onClick={() => {
                        setLoading(true);
                        fetchSofiaStatus();
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold rounded-lg transition-colors"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Retry
                </button>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col p-1">
            {/* Connection Status Header */}
            <div
                className={`flex items-center justify-between p-3 rounded-lg border mb-4 transition-all ${
                    data.online
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                        : 'bg-destructive/10 border-destructive/30 text-destructive'
                }`}
            >
                <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-full ${data.online ? 'bg-emerald-500/20' : 'bg-destructive/20'}`}>
                        <Radio className={`w-4 h-4 ${data.online ? 'animate-pulse' : ''}`} />
                    </div>
                    <span className="text-sm font-semibold">
                        {data.online ? 'FreeSWITCH Sofia Engine Online' : 'FreeSWITCH Sofia Engine Offline'}
                    </span>
                </div>
                <span className="text-xs font-medium px-2 py-0.5 bg-black/10 rounded-full">
                    {data.profilesCount} Profiles
                </span>
            </div>

            {/* Profiles List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                {data.profiles.length > 0 ? (
                    data.profiles.map((profile) => {
                        const isRunning = profile.state.toLowerCase().includes('running');
                        return (
                            <button
                                key={profile.name}
                                onClick={() => handleProfileClick(profile.name)}
                                className="w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all hover:translate-x-1 duration-200 group"
                                style={{
                                    backgroundColor: cardColor,
                                    borderColor: borderColor
                                }}
                            >
                                <div className="flex flex-col gap-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                            {profile.name}
                                        </span>
                                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.2 rounded-full ${
                                            isRunning
                                                ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                                : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                                        }`}>
                                            {profile.state}
                                        </span>
                                    </div>
                                    <span className="text-xs text-muted-foreground font-mono truncate max-w-[250px]">
                                        {profile.data}
                                    </span>
                                </div>
                                <Terminal className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-2" />
                            </button>
                        );
                    })
                ) : (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                        {data.error || 'No active profiles found.'}
                    </div>
                )}
            </div>

            {/* Profile Configuration Modal */}
            <Modal
                isOpen={selectedProfile !== null}
                onClose={handleCloseModal}
                title={`Sofia Profile Configuration: ${selectedProfile}`}
                size="lg"
            >
                {loadingConfig && (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                        <span className="text-sm text-muted-foreground">Fetching profile details...</span>
                    </div>
                )}

                {configError && (
                    <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm font-medium">{configError}</span>
                    </div>
                )}

                {profileConfig && (
                    <div className="relative">
                        <div className="absolute right-3 top-3 flex items-center gap-1.5 text-xs text-muted-foreground px-2.5 py-1 bg-black/30 rounded-md border border-white/5 font-mono select-none">
                            <Cpu className="w-3.5 h-3.5" /> sofia status profile {selectedProfile}
                        </div>
                        <pre className="w-full bg-[#090d16] text-[#c9d1d9] p-5 rounded-lg border border-white/10 font-mono text-xs overflow-x-auto max-h-[60vh] overflow-y-auto leading-relaxed shadow-inner">
                            {profileConfig}
                        </pre>
                    </div>
                )}
            </Modal>
        </div>
    );
}
